import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedWithPartial, UserController, UserEntity } from '@domain';
import { RaInputComponent, RaLoadingComponent } from '@shared/components';
import { TranslatePipe } from '@ngx-translate/core';
import { RaCheckboxComponent } from 'ra-ui';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';

/**
 * Modal "Quem pode acessar" — define quem pode acessar um documento privado.
 *
 * Lista os MEMBROS da empresa (checkbox, pré-marcados pelo `sharedWith` atual) e
 * permite ADICIONAR e-mails avulsos (externos, ex.: um cliente). E-mails externos
 * já existentes em `sharedWith` (que não são membros) entram pré-carregados na
 * lista de adicionados, podendo ser removidos.
 */
@Component({
    selector: 'modal-document-access',
    templateUrl: 'modal-document-access.component.html',
    styleUrl: 'modal-document-access.component.scss',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RaModalComponent,
        RaButtonComponent,
        RaInputComponent,
        RaCheckboxComponent,
        RaIconComponent,
        RaLoadingComponent,
        TranslatePipe,
    ],
})
export class ModalDocumentAccessComponent implements OnInit {
    /** Lista atual de quem pode acessar (fonte da pré-seleção). */
    @Input() sharedWith: SharedWithPartial[] = [];
    /** Loading do save (controlado pelo pai). */
    public loading = input<boolean>(false);
    public close = output<void>();
    public save = output<SharedWithPartial[]>();

    private _userController = inject(UserController);
    private _destroyRef = inject(DestroyRef);

    public members = signal<UserEntity[]>([]);
    public loadingMembers = signal<boolean>(true);
    public selectedEmails = signal<Set<string>>(new Set<string>());
    /** E-mails avulsos (externos) adicionados manualmente. */
    public extraEmails = signal<SharedWithPartial[]>([]);

    /** Campo para adicionar um novo e-mail. */
    public newEmailControl = new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
    });

    ngOnInit(): void {
        // Pré-marca os emails já presentes em `sharedWith` (case-insensitive).
        const initial = new Set(
            (this.sharedWith ?? [])
                .map(s => this._normalize(s.email))
                .filter(Boolean)
        );
        this.selectedEmails.set(initial);

        this._userController
            .getEmployees()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: members => {
                    this.members.set(members ?? []);
                    // E-mails de `sharedWith` que NÃO são membros viram "adicionados".
                    const memberEmails = new Set(
                        (members ?? []).map(m => this._normalize(m.email))
                    );
                    this.extraEmails.set(
                        (this.sharedWith ?? []).filter(
                            s => !memberEmails.has(this._normalize(s.email))
                        )
                    );
                    this.loadingMembers.set(false);
                },
                error: () => this.loadingMembers.set(false),
            });
    }

    public isSelected(email: string): boolean {
        return this.selectedEmails().has(this._normalize(email));
    }

    public toggleMember(email: string): void {
        const key = this._normalize(email);
        if (!key) return;

        const next = new Set(this.selectedEmails());
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        this.selectedEmails.set(next);
    }

    /** Adiciona o e-mail digitado: se for um membro, marca-o; senão vira e-mail externo. */
    public addEmail(): void {
        if (this.newEmailControl.invalid) {
            this.newEmailControl.markAsTouched();
            return;
        }

        const raw = this.newEmailControl.value.trim();
        const key = this._normalize(raw);
        if (!key) return;

        // Se já é membro, apenas marca o checkbox.
        const member = this.members().find(m => this._normalize(m.email) === key);
        if (member) {
            const next = new Set(this.selectedEmails());
            next.add(key);
            this.selectedEmails.set(next);
            this.newEmailControl.reset('');
            return;
        }

        // Evita duplicar na lista de externos.
        const exists = this.extraEmails().some(e => this._normalize(e.email) === key);
        if (!exists) {
            this.extraEmails.update(list => [...list, { name: '', email: raw }]);
        }
        this.newEmailControl.reset('');
    }

    public removeExtra(email: string): void {
        const key = this._normalize(email);
        this.extraEmails.update(list =>
            list.filter(e => this._normalize(e.email) !== key)
        );
    }

    /** Edita um e-mail externo: devolve ao campo e remove da lista (re-adicionar salva). */
    public editExtra(extra: SharedWithPartial): void {
        this.newEmailControl.setValue(extra.email || '');
        this.removeExtra(extra.email);
    }

    public onSave(): void {
        // Membros selecionados (gerenciados pelos checkboxes).
        const selectedMembers: SharedWithPartial[] = this.members()
            .filter(m => this.isSelected(m.email))
            .map(m => ({ name: m.name, email: m.email }));

        // Junta membros + e-mails externos, deduplicando por e-mail (membro precede).
        const seen = new Set(selectedMembers.map(m => this._normalize(m.email)));
        const merged = [...selectedMembers];
        for (const extra of this.extraEmails()) {
            const key = this._normalize(extra.email);
            if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(extra);
            }
        }

        this.save.emit(merged);
    }

    private _normalize(email?: string): string {
        return (email || '').toLowerCase().trim();
    }
}
